import { FileText, Truck, PackageCheck, ClipboardCheck, ShoppingCart, HelpCircle, CircleDot, Loader, Hourglass, CheckCircle2, Archive, XCircle } from "lucide-react";

// Ticket categories, statuses, priorities — ids must match hasbroPortalController on the backend
export const CATEGORIES = [
    { id: "asn", label: "ASN", icon: FileText },
    { id: "transport", label: "Transport / carrier", icon: Truck },
    { id: "shipping_status", label: "Shipped or not", icon: PackageCheck },
    { id: "pod", label: "Proof of delivery (POD)", icon: ClipboardCheck },
    { id: "order", label: "Outbound order", icon: ShoppingCart },
    { id: "other", label: "Other", icon: HelpCircle },
];

// tone = Fiori semantic colour (see fiori.jsx TONES). `customer` = wording a customer sees.
export const STATUSES = [
    { id: "open", label: "New", tone: "information", icon: CircleDot },
    { id: "in_progress", label: "In progress", tone: "critical", icon: Loader },
    { id: "awaiting_customer", label: "Awaiting customer", customer: "Awaiting your reply", tone: "indication", icon: Hourglass },
    { id: "resolved", label: "Resolved", tone: "positive", icon: CheckCircle2 },
    { id: "closed", label: "Closed", tone: "neutral", icon: Archive },
    { id: "cancelled", label: "Cancelled", tone: "neutral", icon: XCircle },
];
export const DONE_STATUSES = new Set(["resolved", "closed", "cancelled"]);

// Priority = impact × urgency (backend PRIORITY_MATRIX); SLA targets come from the backend
export const PRIORITIES = {
    1: { label: "P1 — Critical", short: "Critical", tone: "negative" },
    2: { label: "P2 — High", short: "High", tone: "critical" },
    3: { label: "P3 — Moderate", short: "Moderate", tone: "information" },
    4: { label: "P4 — Low", short: "Low", tone: "neutral" },
};
export const PRIORITY_MATRIX = { "1-1": 1, "1-2": 2, "1-3": 3, "2-1": 2, "2-2": 3, "2-3": 4, "3-1": 3, "3-2": 4, "3-3": 4 };
export const SLA_HOURS = { 1: { response: 1, resolution: 8 }, 2: { response: 4, resolution: 24 }, 3: { response: 8, resolution: 72 }, 4: { response: 24, resolution: 120 } };
export const IMPACT = [
    { value: 1, label: "Many orders or stores", hint: "A whole delivery run, DC or several stores" },
    { value: 2, label: "One order or store", hint: "A single PO, consignment or store" },
    { value: 3, label: "A question or one item", hint: "Information only, nothing is blocked" },
];
export const URGENCY = [
    { value: 1, label: "Today", hint: "Delivery is due now or a store is waiting" },
    { value: 2, label: "In the next few days", hint: "It needs sorting this week" },
    { value: 3, label: "No rush", hint: "Whenever you can" },
];
export const priorityOf = (impact, urgency) => PRIORITY_MATRIX[`${impact}-${urgency}`] || 3;

// Progress tracker stages (backend `stage`: 1 submitted … 5 closed)
export const STAGES = ["Submitted", "Assigned", "In progress", "Resolved", "Closed"];

export const categoryOf = id => CATEGORIES.find(c => c.id === id) || CATEGORIES[CATEGORIES.length - 1];
export const categoryLabel = id => categoryOf(id).label;
export const statusOf = id => STATUSES.find(s => s.id === id) || STATUSES[0];
export const ticketNumber = n => `CS${String(n ?? 0).padStart(7, "0")}`;

// Staff should answer: the customer wrote last and the ticket isn't done
export const needsReply = t => t.lastMessageBy === "customer" && !DONE_STATUSES.has(t.status);

const hoursText = ms => {
    const h = Math.abs(ms) / 3600000;
    return h < 1 ? `${Math.max(1, Math.round(h * 60))} min` : h < 48 ? `${Math.round(h)} h` : `${Math.round(h / 24)} days`;
};
/** One SLA clock → { text, tone } */
export function slaState(clock) {
    if (!clock) return { text: "—", tone: "neutral" };
    if (clock.at) return clock.met ? { text: "Met", tone: "positive" } : { text: "Missed", tone: "negative" };
    if (clock.breached) return { text: `Overdue ${hoursText(clock.remainingMs)}`, tone: "negative" };
    if (clock.remainingMs != null && clock.remainingMs < 2 * 3600000) return { text: `Due in ${hoursText(clock.remainingMs)}`, tone: "critical" };
    return { text: `Due in ${hoursText(clock.remainingMs ?? 0)}`, tone: "neutral" };
}

export const fmtDateTime = v => (v ? new Date(v).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
export const apiError = (err, fallback) => err?.response?.data?.error || fallback;
